/**
 * Sound Storage Configuration
 *
 * Provides URLs for sound files, using Supabase Storage in production
 * and local files in development.
 */
import { supabase } from './supabase';
const SUPABASE_URL = 'https://spnoztsuvgxrdmkeygdu.supabase.co';
const STORAGE_BUCKET = 'sounds';
/**
 * Get the URL for a sound file
 * @param path - Path to the sound file relative to /sounds/ (e.g., 'rain-woodlands.m4a' or 'og/Scroll 1.m4a')
 * @returns Full URL to the sound file
 */
export function getSoundUrl(path) {
    // Always use Supabase Storage (both in development and production)
    return `${SUPABASE_URL}/storage/v1/object/public/${STORAGE_BUCKET}/${path}`;
}
/**
 * List all available sounds from Supabase Storage
 * @returns Array of sound file paths
 */
export async function listAvailableSounds() {
    try {
        const { data, error } = await supabase.storage
            .from(STORAGE_BUCKET)
            .list('', {
            limit: 1000,
            offset: 0,
            sortBy: { column: 'name', order: 'asc' },
        });
        if (error) {
            console.error('Error listing sounds from Supabase:', error);
            return [];
        }
        if (!data) {
            return [];
        }
        // Filter to only audio files and flatten nested folders
        const audioFiles = [];
        const processFiles = (files, prefix = '') => {
            files.forEach((file) => {
                const fullPath = prefix ? `${prefix}/${file.name}` : file.name;
                if (file.id && !file.metadata) {
                    // It's a folder, recursively list its contents
                    // Note: This would require recursive API calls
                    // For now, we'll skip folders
                }
                else if (file.name) {
                    // It's a file - check if it's an audio file
                    const audioExtensions = ['.mp3', '.m4a', '.wav', '.ogg', '.aac', '.flac'];
                    const isAudioFile = audioExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));
                    if (isAudioFile) {
                        audioFiles.push(fullPath);
                    }
                }
            });
        };
        processFiles(data);
        return audioFiles;
    }
    catch (err) {
        console.error('Error listing sounds:', err);
        return [];
    }
}
/**
 * Preload a sound file
 * @param url - URL of the sound file
 * @returns Promise that resolves when the sound is loaded
 */
export async function preloadSound(url) {
    return new Promise((resolve, reject) => {
        const audio = new Audio();
        audio.addEventListener('canplaythrough', () => resolve(), { once: true });
        audio.addEventListener('error', reject, { once: true });
        audio.src = url;
        audio.load();
    });
}
